import BaseAPI from 'utils/api/base';

import {
  PaginationParams,
  APIRequest,
  UuidableName,
  SearchResponse,
  OrderingParams,
  QueryParams,
  BackgroundOption,
} from 'utils/api/types';

import { env } from 'config';

import { ActivityMapQueryParams } from 'stores/ActivityMap/types';

import { NotificationTarget } from 'core/constants';

import {
  ActiveUserResponse,
  NotificationSettingsResponse,
  NotificationResponse,
  NotificationSettings,
  UserSearchResponse,
  EditUserResponse,
  UserDetailsResponse,
  UserListPaginatedResponse,
  UsersCreationInBulkParams,
  UserProfileResponse,
  UpdateUserRequest,
  UsersCreationParams,
  DeleteUsersRequest,
  KnockTokenResponse,
  NpsResponseRequest,
  TiptapUserTokenResponse,
  UserTimezoneSetting,
  UserTimezoneUpdate,
  UserFieldOption,
  UserFieldDropdownKind,
  UserFieldGridRow,
  UpdateUserFieldGridRowRequest,
  EditUserRequest,
} from './user.types';

class UserAPI extends BaseAPI {
  changePassword = (data: { oldPassword?: string; newPassword1?: string; newPassword2?: string }): APIRequest<void> => {
    const url = '/user/password-change/';
    const method = 'POST';

    return this.request<void>({
      url,
      method,
      data,
      baseURL: env.REACT_APP_ADMIN_API_URL,
    });
  };

  profile = (): APIRequest<UserProfileResponse> => {
    const url = '/user/';
    const method = 'GET';

    return this.request<UserProfileResponse>({
      url,
      method,
    });
  };

  createUser = (data: UsersCreationParams): APIRequest<UsersCreationParams> => {
    const url = '/users/single/';
    const method = 'POST';

    return this.request<UsersCreationParams>({
      url,
      method,
      data,
      baseURL: env.REACT_APP_ADMIN_API_URL,
    });
  };

  createUserInBulk = (data: UsersCreationInBulkParams): APIRequest<UsersCreationInBulkParams> => {
    const url = '/users/import/';
    const method = 'POST';

    return this.request<UsersCreationInBulkParams>({
      url,
      method,
      data,
      baseURL: env.REACT_APP_ADMIN_API_URL,
    });
  };

  loadUsers = (params: OrderingParams | QueryParams): APIRequest<UserListPaginatedResponse> => {
    const url = '/users/';
    const method = 'GET';

    return this.request<UserListPaginatedResponse>({
      url,
      method,
      params,
    });
  };

  loadUserData = (userId: string): APIRequest<UserDetailsResponse> => {
    const url = `/users/${userId}/`;
    const method = 'GET';

    return this.request<UserDetailsResponse>({
      url,
      method,
    });
  };

  loadUserDataForManagers = (userId: string): APIRequest<EditUserResponse> => {
    const url = `/users/${userId}/user_for_managers`;
    const method = 'GET';

    return this.request<EditUserResponse>({
      url,
      method,
    });
  };

  loadUserDetails = (userId: string): APIRequest<UserDetailsResponse> => {
    const url = `/users/${userId}/details/`;
    const method = 'GET';

    return this.request<UserDetailsResponse>({
      url,
      method,
    });
  };

  loadUserDetailsForManagers = (userId: string): APIRequest<UserDetailsResponse> => {
    const url = `/users/${userId}/details_for_managers/`;
    const method = 'GET';

    return this.request<UserDetailsResponse>({
      url,
      method,
    });
  };

  updateUser = (userId: string, data: Partial<UpdateUserRequest>): APIRequest<EditUserResponse> => {
    const url = `/users/${userId}/`;
    const method = 'PATCH';

    return this.request<EditUserResponse>({
      url,
      method,
      data,
    });
  };

  deleteUsers = (data: Partial<DeleteUsersRequest>): APIRequest<void> => {
    const url = `/users/`;
    const method = 'DELETE';

    return this.request<void>({
      url,
      method,
      data,
    });
  };

  updateUserData = (
    userId: string,
    data: EditUserRequest | { tags: { name: string }[] }
  ): APIRequest<EditUserResponse> => {
    const url = `/users/${userId}/`;
    const method = 'PATCH';

    return this.request<EditUserResponse>({
      url,
      method,
      data,
    });
  };

  loadActiveUsers = (params: ActivityMapQueryParams, timeout?: number): APIRequest<ActiveUserResponse[]> => {
    const url = '/users/active/';
    const method = 'GET';

    return this.request<ActiveUserResponse[]>({
      url,
      method,
      params,
      timeout,
    });
  };

  searchUsers = (
    params?: Partial<{
      query: string;
      limit: number;
      public_uuid?: string;
      role?: string;
      licenses?: string;
      exclude_member_group?: string;
    }>
  ): APIRequest<UserSearchResponse[]> => {
    const url = '/users/search/';
    const method = 'GET';

    return this.request<UserSearchResponse[]>({
      url,
      method,
      params,
    });
  };

  searchUserTags = (
    params?: Partial<{
      query: string;
      limit: number;
    }>
  ): APIRequest<SearchResponse[]> => {
    const url = '/users/tags/search/';
    const method = 'GET';

    return this.request<SearchResponse[]>({
      url,
      method,
      params,
    });
  };

  userTagsDetails = (uuid: string): APIRequest<UuidableName> => {
    const url = `/users/tags/${uuid}/`;
    const method = 'GET';

    return this.request<UuidableName>({
      url,
      method,
    });
  };

  loadNotifications = (params: PaginationParams & { target: string }): APIRequest<NotificationResponse> => {
    const url = '/user/notifications/';
    const method = 'GET';

    return this.request<NotificationResponse>({
      url,
      method,
      params,
    });
  };

  dismissNotifications = (data: string[]): APIRequest<void> => {
    const url = '/user/notifications/';
    const method = 'PUT';
    return this.request<void>({
      url,
      method,
      data,
    });
  };

  loadNotificationSettings = (): APIRequest<NotificationSettingsResponse> => {
    const method = 'GET';
    const url = '/user/notifications/settings/';

    return this.request<NotificationSettingsResponse>({
      url,
      method,
    });
  };

  updateNotificationSetting = (
    notificationTarget: NotificationTarget,
    settings: Partial<NotificationSettings>
  ): APIRequest<NotificationSettings> => {
    const method = 'PATCH';
    const url = `/user/notifications/settings/${notificationTarget}/`;

    return this.request<NotificationSettings>({
      url,
      method,
      data: settings,
    });
  };
  getKnockToken = (options?: BackgroundOption): APIRequest<KnockTokenResponse> => {
    const url = '/user/knock-token/';
    const method = 'GET';

    return this.request<KnockTokenResponse>({ url, method, ...options });
  };

  submitNpsResponse = (data: NpsResponseRequest, signal?: AbortSignal): APIRequest<void> => {
    const url = '/user/nps-response/';
    const method = 'POST';

    return this.request<void>({ url, method, data, signal });
  };

  getTiptapUserToken = (signal?: AbortSignal, options?: BackgroundOption): APIRequest<TiptapUserTokenResponse> => {
    const url = '/user/tiptap-token/';
    const method = 'GET';

    return this.request<TiptapUserTokenResponse>({ url, method, signal, ...options });
  };

  getTimezone = (signal?: AbortSignal): APIRequest<UserTimezoneSetting> => {
    const url = '/user/timezone/';
    const method = 'GET';

    return this.request<UserTimezoneSetting>({ url, method, signal });
  };

  updateTimezone = (data: UserTimezoneUpdate): APIRequest<UserTimezoneSetting> => {
    const url = '/user/timezone/';
    const method = 'PATCH';

    return this.request<UserTimezoneSetting>({ url, method, data });
  };

  // `state` is null when nothing has been saved for this grid yet.
  loadGridPreferences = (gridKey: string, timeout?: number): APIRequest<{ state: Record<string, unknown> | null }> => {
    const url = '/user/grid-preferences/';
    const method = 'GET';

    return this.request<{ state: Record<string, unknown> | null }>({
      url,
      method,
      params: { grid_key: gridKey },
      timeout,
    });
  };

  saveGridPreferences = (gridKey: string, state: object): APIRequest<void> => {
    const url = '/user/grid-preferences/';
    const method = 'PUT';

    return this.request<void>({
      url,
      method,
      params: { grid_key: gridKey },
      data: { state },
    });
  };

  sendPasswordReset = (userId: string): APIRequest<{ detail: string }> => {
    const url = `/users/${userId}/password-reset/send/`;
    const method = 'POST';

    return this.request<{ detail: string }>({
      url,
      method,
      baseURL: env.REACT_APP_ADMIN_API_URL,
    });
  };

  resendInvite = (userId: string): APIRequest<{ detail: string }> => {
    const url = `/users/${userId}/resend-invite/`;
    const method = 'POST';

    return this.request<{ detail: string }>({
      url,
      method,
      baseURL: env.REACT_APP_ADMIN_API_URL,
    });
  };

  reactivateUser = (userId: string): APIRequest<{ detail: string }> => {
    const url = `/users/${userId}/reactivate/`;
    const method = 'POST';

    return this.request<{ detail: string }>({
      url,
      method,
      baseURL: env.REACT_APP_ADMIN_API_URL,
    });
  };

  listUserFieldOptions = (
    kind: UserFieldDropdownKind,
    params?: { include_inactive?: boolean }
  ): APIRequest<UserFieldOption[]> => {
    const url = `/user-fields/options/${kind}/`;
    const method = 'GET';

    return this.request<UserFieldOption[]>({
      url,
      method,
      params,
    });
  };

  createUserFieldOption = (
    kind: UserFieldDropdownKind,
    data: { name: string; ordering?: number }
  ): APIRequest<UserFieldOption> => {
    const url = `/user-fields/options/${kind}/`;
    const method = 'POST';

    return this.request<UserFieldOption>({
      url,
      method,
      data,
    });
  };

  updateUserFieldOption = (
    kind: UserFieldDropdownKind,
    uuid: string,
    data: Partial<Pick<UserFieldOption, 'name' | 'ordering' | 'isActive'>>
  ): APIRequest<UserFieldOption> => {
    const url = `/user-fields/options/${kind}/${uuid}/`;
    const method = 'PATCH';

    return this.request<UserFieldOption>({
      url,
      method,
      data,
    });
  };

  deleteUserFieldOption = (kind: UserFieldDropdownKind, uuid: string): APIRequest<void> => {
    const url = `/user-fields/options/${kind}/${uuid}/`;
    const method = 'DELETE';

    return this.request<void>({
      url,
      method,
    });
  };

  loadUserFieldsGrid = (): APIRequest<UserFieldGridRow[]> => {
    const url = `/user-fields/custom-fields/`;
    const method = 'GET';

    return this.request<UserFieldGridRow[]>({
      url,
      method,
    });
  };

  updateUserFieldGridRow = (data: UpdateUserFieldGridRowRequest): APIRequest<UserFieldGridRow> => {
    const url = `/user-fields/custom-fields/`;
    const method = 'PATCH';

    return this.request<UserFieldGridRow>({
      url,
      method,
      data,
    });
  };
}

export default UserAPI;
